/**
 * The "Check Leaderboard" monthly reminder — WHEN it is due, and whether it
 * should be on screen right now. Pure: no I/O, `now` is always a parameter,
 * so `test-reminder-schedule.mjs` can pin every rule without a database.
 *
 * Asked for by Chris, 2026-10-12:
 *   - Due on the 15th of every month.
 *   - If the 15th is a Saturday or Sunday, due the FOLLOWING Monday instead
 *     (Sat 15th → Mon 17th, Sun 15th → Mon 16th).
 *   - Only Renee sees it (`isReminderUser`).
 *   - It pops up when she opens the dashboard on or after the due day, and
 *     KEEPS popping up, day after day, until she marks it done. Snoozing
 *     (30 minutes or 1 hour) hides it only until the snooze runs out.
 *
 * All dates are South Carolina dates (America/New_York), the same way
 * `easternDate()` in sync-rezen.ts decides "today" — so the reminder does not
 * appear at 8 PM on the 14th because UTC has already rolled over.
 */

export const REMINDER_KEY = 'leaderboard'
export const REMINDER_TEXT = 'Check Leaderboard'

/**
 * The first month this reminder exists for. Without a start, the first time
 * the dashboard loads after this ships it would reach back to whatever
 * cycle came before and show a reminder for a month nobody ever asked about.
 */
export const FIRST_PERIOD = '2026-10'

/** Snooze lengths she can pick, in minutes. */
export const SNOOZE_CHOICES = [30, 60] as const

/** 'YYYY-MM-DD' for `now` in Eastern time. */
export function easternToday(now: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** 'YYYY-MM' → the due date in that month, 'YYYY-MM-DD'. */
export function dueDateFor(period: string): string {
  const [y, m] = period.split('-').map(Number)
  // Noon UTC so the weekday is the calendar weekday, never shifted by a zone.
  const d = new Date(Date.UTC(y, m - 1, 15, 12))
  const dow = d.getUTCDay() // 0 = Sunday, 6 = Saturday
  const day = dow === 6 ? 17 : dow === 0 ? 16 : 15
  return `${period}-${String(day).padStart(2, '0')}`
}

function prevPeriod(period: string): string {
  const [y, m] = period.split('-').map(Number)
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`
}

/**
 * The reminder cycle `today` belongs to: this month's, once its due date has
 * arrived; otherwise last month's (which carries on showing, if it was never
 * marked done, right up until this month's takes over). Null before
 * `FIRST_PERIOD`'s due date.
 */
export function currentPeriod(today: string): string | null {
  const thisMonth = today.slice(0, 7)
  const period = today >= dueDateFor(thisMonth) ? thisMonth : prevPeriod(thisMonth)
  return period >= FIRST_PERIOD ? period : null
}

/** What the database holds for one cycle — or null if she has not touched it yet. */
export type ReminderState = {
  period: string
  done_at: string | null
  snoozed_until: string | null
} | null

/**
 * Should the pop-up be on screen right now? Only for the current cycle, only
 * if it is not done, and only once any snooze has run out.
 */
export function shouldShow(now: Date, state: ReminderState): boolean {
  const period = currentPeriod(easternToday(now))
  if (!period) return false
  if (!state || state.period !== period) return true
  if (state.done_at) return false
  if (state.snoozed_until && new Date(state.snoozed_until).getTime() > now.getTime()) return false
  return true
}

/**
 * Renee only. Matched by her sign-in email, with her name as a backstop in
 * case her Google login is a different address than the one listed here.
 */
const REMINDER_EMAILS = ['renee@reneeroark.com', 'renee.roark@therealbrokerage.com']
export function isReminderUser(user: { email?: string | null; full_name?: string | null } | null): boolean {
  if (!user) return false
  const email = (user.email ?? '').trim().toLowerCase()
  if (REMINDER_EMAILS.includes(email)) return true
  return (user.full_name ?? '').trim().toLowerCase() === 'renee roark'
}
